/* Suppliers — handover §14 step 8.

   "The account number is the field you need at 3am and cannot find." That line
   from §6 is the whole design brief, so the account number is not buried in a
   drawer: it is a column, in monospace, and one click copies it. At 3am you are
   reading it to somebody on a support line, not admiring the layout.

   Notice period is the other thing this register exists for. A contract with 90
   days' notice that auto-renews has already renewed by the time it appears in a
   90-day expiry list, so the screen counts down to the *notice* deadline rather
   than the contract end, and says which one it is showing. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { countdown, daysUntil, expiryTone, fmtDate, money } from '../../lib/format';
import { decideBy, noticeMissed } from '../../lib/registers';
import { supplierFields, valuesSeen } from '../../lib/registerFields';
import type { ISupplier } from '../../models';
import { recordHref, useOpenRecord } from '../../hooks/useOpenRecord';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  RelationCard,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  TableSkeleton,
  type Column,
} from '../ui';
import { Copyable } from './Copyable';
import { EditDrawer } from './EditDrawer';
import s from './screens.module.scss';

export function SuppliersScreen() {
  const svc = useServices();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<ISupplier | null>(null);
  /* Open with no record selected means "add". Kept apart from `selected` so a
     half-typed new supplier is not mistaken for an edit of the last one. */
  const [adding, setAdding] = useState(false);

  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });

  /* Arriving from a relation elsewhere. The value is whatever the source
     had to hand -- an id where one is stored, a name where it is not --
     so both have to match, and every record gets a URL somebody can send. */
  useOpenRecord(
    suppliers.data ?? [],
    (row, value) => row.id === value || row.name.toLowerCase() === value.toLowerCase(),
    setSelected,
  );
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const apps = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });

  const close = () => {
    setSelected(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.suppliers });
    void qc.invalidateQueries({ queryKey: qk.audit });
    close();
  };

  const save = useMutation({

    meta: { ok: 'Supplier saved' },
    mutationFn: (values: Record<string, unknown>) => {
      const patch = values as unknown as Omit<ISupplier, 'id'>;
      return selected
        ? svc.suppliers.update(selected.id, patch)
        : svc.suppliers.create(patch);
    },
    onSuccess: refresh,
  });

  const remove = useMutation({

    meta: { ok: 'Supplier deleted' },
    mutationFn: (id: string) => svc.suppliers.remove(id),
    onSuccess: refresh,
  });

  const ownerName = (id: string) =>
    (people.data ?? []).find((p) => p.id === id)?.name ?? (id ? '—' : '');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (suppliers.data ?? []).filter((x) => {
      const decide = daysUntil(decideBy(x));
      if (lens === 'deciding' && !(x.end && decide <= 90)) return false;
      if (lens === 'auto' && !x.auto) return false;
      if (lens === 'passed' && !noticeMissed(x)) return false;
      if (!q) return true;
      return (
        x.name.toLowerCase().includes(q) ||
        x.service.toLowerCase().includes(q) ||
        x.acct.toLowerCase().includes(q) ||
        x.support.toLowerCase().includes(q)
      );
    });
  }, [suppliers.data, lens, query]);

  const totals = useMemo(() => {
    const all = suppliers.data ?? [];
    return {
      spend: all.reduce((n, x) => n + x.cost, 0),
      /* Auto-renewing contracts whose notice window has already closed. The
         worst state in this register: the decision was made by not making it. */
      missed: all.filter(noticeMissed).length,
      deciding: all.filter((x) => x.end && daysUntil(decideBy(x)) <= 90 && daysUntil(x.end) >= 0)
        .length,
    };
  }, [suppliers.data]);

  const columns: Column<ISupplier>[] = [
    {
      key: 'name',
      header: 'Supplier',
      sortBy: (x) => x.name,
      render: (x) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-building-store" aria-hidden />
          </span>
          <div>
            <div className={s.cellMain}>{x.name}</div>
            <div className={s.cellSub}>{x.service}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'acct',
      header: 'Account number',
      sortBy: (x) => x.acct,
      // The 3am field. Monospace and one click to copy — see the file comment.
      render: (x) => (x.acct ? <Copyable value={x.acct} /> : <span className={s.cellSub}>—</span>),
    },
    {
      key: 'support',
      header: 'Support',
      sortBy: (x) => x.support,
      render: (x) => <span className={s.mono}>{x.support || '—'}</span>,
    },
    {
      key: 'decide',
      header: 'Decide by',
      sortBy: (x) => decideBy(x),
      /* Not the contract end. A 90-day notice period on an auto-renewing
         contract means the real deadline is three months earlier, and a list
         that shows only the end date lets it pass while looking fine. */
      render: (x) => {
        if (!x.end) return <span className={s.cellSub}>—</span>;
        const decide = decideBy(x);
        const passed = daysUntil(decide) < 0 && daysUntil(x.end) >= 0;
        return (
          <>
            <Badge tone={passed && x.auto ? 'bad' : expiryTone(decide)}>{fmtDate(decide)}</Badge>
            {decide !== x.end && (
              <div className={s.cellSub}>
                {x.notice} notice · ends {fmtDate(x.end)}
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'auto',
      header: 'Renewal',
      sortBy: (x) => (x.auto ? 1 : 0),
      render: (x) => (
        <Badge tone={x.auto ? 'warn' : 'muted'}>{x.auto ? 'Auto-renews' : 'Manual'}</Badge>
      ),
    },
    {
      key: 'cost',
      priority: 3,
      header: 'A year',
      sortBy: (x) => x.cost,
      render: (x) => (x.cost > 0 ? money(x.cost) : <span className={s.cellSub}>—</span>),
    },
    { key: 'owner', header: 'Owner', sortBy: (x) => ownerName(x.owner), render: (x) => ownerName(x.owner) },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Suppliers</h1>
          <p>
            Contracts, account numbers and support lines. Dates count down to the notice deadline, not the contract end.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setSelected(null); setAdding(true); }}>
          New supplier
        </Button>
      </div>

      {suppliers.isError && <LoadError error={suppliers.error} what="suppliers" />}

      <StatGrid>
        <StatCard
          icon="ti-building-store"
          label="Suppliers"
          value={suppliers.isLoading ? '—' : (suppliers.data?.length ?? 0)}
          tone="info"
          foot="Under contract"
        />
        <StatCard
          icon="ti-receipt"
          label="Contracted a year"
          value={suppliers.isLoading ? '—' : money(totals.spend)}
          tone="info"
          foot="Across every supplier"
        />
        <StatCard
          icon="ti-calendar-exclamation"
          label="Decisions inside 90 days"
          value={suppliers.isLoading ? '—' : totals.deciding}
          tone={totals.deciding > 0 ? 'warn' : 'ok'}
          foot={totals.deciding > 0 ? 'Notice windows closing' : 'Nothing pressing'}
        />
        <StatCard
          icon="ti-alarm-off"
          label="Notice window missed"
          value={suppliers.isLoading ? '—' : totals.missed}
          tone={totals.missed > 0 ? 'bad' : 'ok'}
          foot={totals.missed > 0 ? 'Already auto-renewed' : 'None missed'}
        />
      </StatGrid>

      <Controls count={`${rows.length} of ${suppliers.data?.length ?? 0}`}>
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Supplier, service, account number, support line…"
        />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'deciding', label: 'Decide inside 90 days' },
            { value: 'passed', label: 'Notice missed' },
            { value: 'auto', label: 'Auto-renewing' },
          ]}
        />
      </Controls>

      <Panel flush>
        {suppliers.isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(x) => x.id}
            initialSort={{ key: 'decide', dir: 'asc' }}
            onRowClick={setSelected}
            empty={
              <EmptyState icon="ti-building-store" title="No suppliers match">
                Try clearing the filters.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null || adding}
        record={selected}
        fields={supplierFields(valuesSeen(suppliers.data, ['service', 'notice']))}
        lookups={{ people: people.data ?? [], suppliers: suppliers.data ?? [] }}
        noun="supplier"
        icon="ti-building-store"
        meta={
          selected && (
            <>
              <Badge tone={selected.auto ? 'warn' : 'muted'}>
                {selected.auto ? 'Auto-renews' : 'Manual renewal'}
              </Badge>
              {selected.end && (
                <Badge tone={expiryTone(decideBy(selected))}>
                  Decide {countdown(decideBy(selected))}
                </Badge>
              )}
            </>
          )
        }
        onClose={close}
        onSave={(values) => save.mutate(values)}
        onDelete={selected ? () => remove.mutate(selected.id) : undefined}
        saving={save.isPending}
        deleting={remove.isPending}
        error={save.error}
      >
        {selected?.acct && (
          <div>
            <div className={s.sectionLabel}>Account number</div>
            <Copyable value={selected.acct} large />
          </div>
        )}
        {selected && (() => {
          /* What this supplier actually provides. Answers the question a
             renewal starts with — "if we drop them, what stops working" —
             which is otherwise a search of another register. */
          const supplied = (apps.data ?? []).filter(
            (a) => a.supplier === selected.id || a.supplier === selected.name,
          );
          return (
            <RelationCard
              icon="ti-apps"
              title="Applications supplied"
              count={supplied.length}
              preview={supplied.slice(0, 3).map((a) => a.name).join(' · ')}
              defaultOpen
            >
              {supplied.map((a) => (
                <a key={a.id} className={`${s.row} ${s.rowFlush}`} href={recordHref('/applications', a.name)}>
                  <div className={s.rowIcon}>
                    <i className="ti ti-app-window" aria-hidden />
                  </div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{a.name}</div>
                    <div className={s.rowSub}>
                      {a.purpose} · {a.users > 0 ? `${a.users} users` : 'users not recorded'}
                    </div>
                  </div>
                </a>
              ))}
            </RelationCard>
          );
        })()}

        {selected && noticeMissed(selected) && (
          <div className={s.ownedNote}>
            <i className="ti ti-alarm-off" aria-hidden />
            <span>
              The notice window closed on {fmtDate(decideBy(selected))} and this contract
              auto-renews. Cancelling now takes effect at the end of the next term, not this one.
            </span>
          </div>
        )}
      </EditDrawer>
    </>
  );
}
